import { Box, useTheme } from '@material-ui/core';
import { Backdrop, Switch, useConfirmationModal } from '@room-match/shared-ui-components';
import AsyncCompositeCard from 'components/AsyncCompositeCard/AsyncCompositeCard';
import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import MediaBinModal from 'shared/components/MediaBinModal/MediaBinModal';
import { IComposite, INITIAL_COMPOSITE_STATES } from 'shared/constants/INITIAL_COMPOSITE_STATES';
import StyledDialog from 'shared/elements/StyledDialog/StyledDialog';
import { useAgencyTalentPermission } from 'shared/hooks/useAgencyTalentPermission';
import { IGeneralSection } from 'shared/interfaces/IGeneral';
import { ITalentUpdatePayload } from 'shared/interfaces/ITalent';
import { mediaService } from 'shared/services/mediaService';
import { profileService } from 'shared/services/profileService';
import { talentService } from 'shared/services/talentService';
import { uploadService } from 'shared/services/uploadService';
import EditCompCardInfo from './EditCompCardInfo';
import { compositeCardStyles } from './styles'; // useConfirmationModal
import { useLocation, useHistory } from 'react-router-dom';

const { getMediaProfile, getSingleProfileGraphQL, getSingleProfile, updateProfile, getProfileTabDetail } =
  profileService();
const { getStatistics, getGeneral } = talentService();
const { uploadMedia } = uploadService();
const { getMediaList } = mediaService();

const CompCard = ({ permissions, nonRepre }: { permissions: string[]; nonRepre: boolean }) => {
  const location = useLocation();
  const history = useHistory();
  const { profileId } = useParams() as { profileId: string };
  const queryClient = useQueryClient();
  const theme = useTheme();
  const themeType = theme.palette.type;
  const styles = compositeCardStyles(theme);
  const [isInitialCompositeDataIsNull, setIsInitialCompositeDataIsNull] = useState(true);
  const { data: generalData, isLoading: isGeneralLoading } = getGeneral();
  const { data: statisticsData, isLoading: isStatisticsLoading } = getStatistics();
  const { data: profileData, isLoading: profileLoading } = getSingleProfileGraphQL(profileId);
  const { mutateAsync: updateProfileMutateAsync, isLoading: isLoadingUpdateProfile } = updateProfile();
  const { data: compositeCardData, isLoading: isLoadingCompositeCardData } = getProfileTabDetail(
    profileId,
    'composite_card',
  );

  const {
    ConfirmationModal: ConfirmationModal,
    handleOpen: handleOpenConfirm,
    handleSetMessage: handleConfirmSetMessage,
    isOpen: isConfirmModalOpen,
  } = useConfirmationModal({
    type: 'warning',
  });

  const [talentInfo, setTalentInfo] = useState<ITalentUpdatePayload>();
  const [showEditInfo, setShowEditInfo] = useState(false);

  const { isNotifOpen, NotificationModal, handleAgencyTalentPermissions } = useAgencyTalentPermission(
    permissions,
    nonRepre,
  );

  const profileInfo = useMemo(() => {
    if (profileData) {
      return profileData.data.profile;
    }
    return null;
  }, [profileData]);

  useEffect(() => {
    if (isGeneralLoading || isStatisticsLoading) return;
    if (generalData && generalData.data.attributes.general) {
      setTalentInfo((prev) => ({
        ...prev,
        general: generalData.data.attributes.general as IGeneralSection,
      }));
    }
    if (statisticsData && statisticsData.data.attributes.statistics) {
      setTalentInfo((prev) => ({
        ...prev,
        statistics: statisticsData.data.attributes.statistics,
      }));
    }
  }, [generalData, isGeneralLoading, statisticsData, isStatisticsLoading]);

  const compositeCardProps = {
    profileId,
    compositeCardData: compositeCardData,
    isLoadingCompositeCardData,
    queryClient: queryClient,
    uploadMedia: uploadMedia,
    getMediaList: getMediaList,
    getMediaProfile: getMediaProfile,
    getSingleProfile: getSingleProfile,
    updateProfile: updateProfile,
    getStatistics: getStatistics,
    getGeneral: getGeneral,
    getProfileTabDetail: getProfileTabDetail,
    theme: themeType,
    mediaBinModalComponent: MediaBinModal,
    editInfo: () => setShowEditInfo(true),
  };

  const updateCompCard = useCallback(
    async (val: IComposite) => {
      await updateProfileMutateAsync({
        profileId,
        payload: {
          composite_card: {
            ...compositeCardData?.data.attributes.composite_card,
            ...val,
          },
        },
      });
    },
    [compositeCardData?.data.attributes.composite_card, profileId, updateProfileMutateAsync],
  );

  useEffect(() => {
    if (isLoadingCompositeCardData === true) return;
    if (compositeCardData?.data.attributes.composite_card === null) {
      setIsInitialCompositeDataIsNull(true);
      const update = async () => {
        await updateCompCard(INITIAL_COMPOSITE_STATES);
        await queryClient.invalidateQueries(['profile_tab', profileId, 'composite_card'], {
          refetchInactive: true,
        });
      };
      update();
    } else {
      setIsInitialCompositeDataIsNull(false);
    }
  }, [isLoadingCompositeCardData, compositeCardData, updateCompCard, profileId, queryClient]);

  useEffect(() => {
    if (!permissions?.includes('Composite Card') && !nonRepre) {
      const pathnameParts = location.pathname.split('/');
      pathnameParts[pathnameParts.length - 1] = 'images_video_audio';
      const newPathname = pathnameParts.join('/');
      history.replace(newPathname);
    }
  }, [permissions?.length]);

  const handleSave = async (
    val: Array<{
      key: string;
      value: string;
    }>,
  ) => {
    await updateCompCard({
      info: val,
    });
    await queryClient.invalidateQueries(['talents/statistics']);
    setShowEditInfo(false);
    await queryClient.invalidateQueries(['profile_tab']);
    await queryClient.invalidateQueries(['my_data']);
  };

  const toggleEnable = useCallback(async () => {
    const isNonRepre = profileInfo?.representation_type !== 'agency_representation';
    const toggleCompositeCard = async () => {
      const currentValue = compositeCardData?.data.attributes.composite_card?.enabled;
      await updateCompCard({
        enabled: !currentValue,
      });
      await queryClient.invalidateQueries(['profile_tab', profileId, 'composite_card'], {
        refetchInactive: true,
      });
    };
    if (isNonRepre) {
      await toggleCompositeCard();
    } else {
      handleAgencyTalentPermissions({
        module: 'composite_card',
        callback: async () => {
          await toggleCompositeCard();
        },
        ctaText: 'OK',
      });
    }
  }, [
    compositeCardData,
    profileId,
    queryClient,
    handleAgencyTalentPermissions,
    updateCompCard,
    profileInfo?.representation_type,
  ]);

  const sizeGeneralCombined = () => {
    //combine general and statistics flat map
    const general = generalData?.data.attributes.general;
    const statistics = statisticsData?.data.attributes.statistics;
    const combined = { ...statistics, ...general };
    return combined;
  };

  const talentInfoParsed = () => {
    //using composite_card.info to map over sizeGeneralCombined returning the key value pairs
    const info = compositeCardData?.data.attributes.composite_card?.info;
    const combined: any = sizeGeneralCombined();

    if (!info) return [];
    return info.map((item) => {
      return {
        key: item.value,
        value: combined[item.key],
      };
    });
  };

  const finalTalentInfo = () => {
    const stageName = profileInfo?.talent.stage_name;
    const isNonRepre = profileInfo?.representation_type !== 'agency_representation';
    return {
      name: stageName,
      agencyInfo: isNonRepre
        ? null
        : {
            name: profileInfo?.agency?.name,
            email: profileInfo?.agency?.email,
            phone: profileInfo?.agency?.contact_number,
          },
      talentInfo: talentInfoParsed(),
      nonRepreInfo: {
        email: profileInfo?.talent?.email || '',
        phone: profileInfo?.talent?.other_phone_numbers[0]?.number || '',
      },
    };
  };

  const handleClick = () => {
    handleOpenConfirm();
    handleConfirmSetMessage(
      'Exit Confirmation',
      `You have unsaved work in progress, this will discard all the changes you made. Do you wish to continue?`,
      'Yes',
    );
    return;
  };

  return (
    <Fragment>
      {isNotifOpen && <NotificationModal />}
      {isConfirmModalOpen && <ConfirmationModal onConfirm={() => setShowEditInfo(false)} />}
      <Backdrop isLoading={isLoadingUpdateProfile || isInitialCompositeDataIsNull} />
      <div style={{ position: 'relative' }}>
        <AsyncCompositeCard {...compositeCardProps} finalTalentInfo={finalTalentInfo()} />
        <Box sx={styles.switchWrapper}>
          <Switch
            disabled={isLoadingCompositeCardData}
            toggle={toggleEnable}
            checked={compositeCardData?.data.attributes.composite_card?.enabled}
          />
          <Box sx={styles.switchTitle}>
            {compositeCardData?.data.attributes.composite_card?.enabled ? 'Enabled' : 'Disabled'}
          </Box>
        </Box>
        {showEditInfo ? (
          <StyledDialog open={showEditInfo} maxWidth="lg" fullWidth>
            <EditCompCardInfo
              handleSave={handleSave}
              onClose={handleClick}
              info={compositeCardData?.data.attributes.composite_card?.info || []}
            />
          </StyledDialog>
        ) : (
          <React.Fragment />
        )}
      </div>
    </Fragment>
  );
};

export default CompCard;
